import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";

const SECTIONS = [
  { id: "acceptance", label: "Acceptance" },
  { id: "definitions", label: "Definitions" },
  { id: "accounts", label: "Accounts" },
  { id: "subscription", label: "Subscription & Billing" },
  { id: "platform-use", label: "Platform Use" },
  { id: "data-ownership", label: "Data Ownership" },
  { id: "confidentiality", label: "Confidentiality" },
  { id: "ip", label: "Intellectual Property" },
  { id: "warranties", label: "Warranties & Disclaimers" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "termination", label: "Termination" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact-legal", label: "Contact" },
];

const p: React.CSSProperties = {
  fontSize: "15px",
  color: "var(--text-muted)",
  lineHeight: "1.8",
  marginBottom: "14px",
};
const h2Style: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: "20px",
  fontWeight: "700",
  color: "var(--text-primary)",
  margin: "48px 0 12px",
  paddingTop: "8px",
};
const h3Style: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: "15px",
  fontWeight: "700",
  color: "var(--text-primary)",
  margin: "24px 0 8px",
};
const divider = (
  <div
    style={{
      height: "1px",
      background: "rgba(15,23,42,0.07)",
      margin: "40px 0",
    }}
  />
);

export default function TermsPage() {
  const [active, setActive] = useState("acceptance");

  useEffect(() => {
    const updateActiveSection = () => {
      const scrollTop =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      const scrollPosition = scrollTop + 180;
      let current = SECTIONS[0].id;

      for (const s of [...SECTIONS].reverse()) {
        const el = document.getElementById(s.id);
        if (!el) continue;

        const sectionTop = el.getBoundingClientRect().top + scrollTop;
        if (sectionTop <= scrollPosition) {
          current = s.id;
          break;
        }
      }

      setActive(current);
    };

    const syncAfterLayout = () => requestAnimationFrame(updateActiveSection);

    syncAfterLayout();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    document.addEventListener("scroll", updateActiveSection, {
      passive: true,
      capture: true,
    });
    window.addEventListener("hashchange", syncAfterLayout);
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      document.removeEventListener("scroll", updateActiveSection, {
        capture: true,
      });
      window.removeEventListener("hashchange", syncAfterLayout);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, []);

  return (
    <Layout>
      <section
        style={{ padding: "32px 0 48px", background: "var(--navy,#0F172A)" }}
      >
        <div className="container">
          <span
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "700",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--orange)",
              marginBottom: "12px",
            }}
          >
            LEGAL
          </span>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px,4vw,42px)",
              fontWeight: "700",
              color: "#fff",
              letterSpacing: "-0.02em",
              marginBottom: "16px",
            }}
          >
            Terms of Service
          </h1>
          <p
            style={{
              fontSize: "15px",
              color: "rgba(255,255,255,0.6)",
              maxWidth: "560px",
              lineHeight: "1.7",
            }}
          >
            These terms govern your access to and use of the Maltora platform.
            Please read them carefully before using the service.
          </p>
        </div>
      </section>

      <section
        style={{
          padding: "64px 0 96px",
          background: "var(--bg-cream,#F7F4EF)",
        }}
      >
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "220px 1fr",
              gap: "64px",
              alignItems: "start",
            }}
          >
            <aside style={{ position: "sticky", top: "88px" }}>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: "12px",
                }}
              >
                ON THIS PAGE
              </p>
              <nav>
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActive(s.id);
                      document.getElementById(s.id)?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                    style={{
                      display: "block",
                      padding: "5px 0 5px 12px",
                      fontSize: "13px",
                      color:
                        active === s.id ? "var(--teal)" : "var(--text-muted)",
                      borderLeft: `2px solid ${active === s.id ? "var(--teal)" : "transparent"}`,
                      textDecoration: "none",
                      lineHeight: "1.5",
                      transition: "all 0.15s",
                    }}
                  >
                    {i + 1}. {s.label}
                  </a>
                ))}
              </nav>
            </aside>

            <div>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid rgba(15,23,42,0.08)",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  marginBottom: "32px",
                }}
              >
                <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                  Last updated: 13 May 2026
                </span>
              </div>
              <div
                style={{
                  background: "rgba(31,95,91,0.06)",
                  border: "1px solid rgba(31,95,91,0.15)",
                  borderRadius: "10px",
                  padding: "18px 22px",
                  marginBottom: "40px",
                }}
              >
                <p style={{ ...p, marginBottom: 0 }}>
                  <strong>Plain-language summary:</strong> Maltora is a SaaS
                  platform for beverage businesses. You own your data. We
                  provide the platform. You pay per your plan. Either party can
                  terminate. Indian law governs these terms.
                </p>
              </div>

              <section id="acceptance">
                <h2 style={h2Style}>1. Acceptance of Terms</h2>
                <p style={p}>
                  By creating an account, accessing, or using any part of the
                  Maltora platform (the &quot;Service&quot;), you
                  (&quot;Customer&quot;, &quot;you&quot;, &quot;your&quot;)
                  agree to be bound by these Terms of Service
                  (&quot;Terms&quot;) and our{" "}
                  <Link
                    to="/privacy"
                    state={{ fromApp: true }}
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    Privacy Policy
                  </Link>
                  . If you are accepting on behalf of a company or other legal
                  entity, you represent that you have the authority to bind that
                  entity.
                </p>
                <p style={p}>
                  If you do not agree to these Terms, you may not use the
                  Service.
                </p>
              </section>
              {divider}

              <section id="definitions">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  2. Definitions
                </h2>
                <ul style={{ paddingLeft: "20px", marginBottom: "14px" }}>
                  {[
                    [
                      "Maltora / We / Us",
                      "Maltora, a product operated by ZapCom Group, providing the beverage operations platform at maltora.app.",
                    ],
                    [
                      "Customer / You",
                      "The business entity or individual who registers for and uses the Service.",
                    ],
                    [
                      "Users",
                      "Individuals authorised by the Customer to access the platform (e.g. operations staff, sales reps, buyers).",
                    ],
                    [
                      "Customer Data",
                      "All data, content, and information submitted to or generated within the platform by the Customer or its Users, including orders, inventory records, payment data, and buyer information.",
                    ],
                    [
                      "Subscription",
                      "The plan selected by the Customer (Starter, Growth, or Enterprise) granting access to the Service for a defined billing period.",
                    ],
                  ].map(([term, def]) => (
                    <li key={term} style={{ ...p, marginBottom: "6px" }}>
                      <strong>{term}:</strong> {def}
                    </li>
                  ))}
                </ul>
              </section>
              {divider}

              <section id="accounts">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  3. Accounts &amp; Registration
                </h2>
                <h3 style={h3Style}>3.1 Eligibility</h3>
                <p style={p}>
                  You must be at least 18 years old and represent a business
                  operating lawfully in the beverage or related distribution
                  sector to create an account.
                </p>
                <h3 style={h3Style}>3.2 Account Security</h3>
                <p style={p}>
                  You are responsible for maintaining the confidentiality of
                  your login credentials. You agree to notify us immediately at{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    support@maltora.app
                  </a>{" "}
                  if you suspect any unauthorised access to your account.
                  Maltora will not be liable for any loss arising from
                  unauthorised use of your account.
                </p>
                <h3 style={h3Style}>3.3 User Management</h3>
                <p style={p}>
                  The Customer is responsible for all activity that occurs under
                  their account and for ensuring that Users comply with these
                  Terms. The Customer&apos;s account administrator may add or
                  remove Users at any time within the limits of their
                  Subscription plan.
                </p>
              </section>
              {divider}

              <section id="subscription">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  4. Subscription &amp; Billing
                </h2>
                {(
                  [
                    [
                      "4.1 Plans",
                      <>
                        Maltora offers Starter, Growth, and Enterprise plans as
                        described on our{" "}
                        <Link
                          to="/pricing"
                          state={{ fromApp: true }}
                          style={{
                            color: "var(--teal)",
                            textDecoration: "none",
                            fontWeight: "500",
                          }}
                        >
                          Pricing page
                        </Link>
                        . Plan features, order volume limits, and included users
                        are as stated at the time of subscription. Maltora
                        reserves the right to update pricing with 30 days&apos;
                        advance written notice.
                      </>,
                    ],
                    [
                      "4.2 Billing Cycle",
                      "Subscriptions are billed monthly or annually in advance depending on the plan selected. Annual subscriptions are non-refundable except where required by applicable law or as stated in Section 4.4.",
                    ],
                    [
                      "4.3 Taxes",
                      "All prices are exclusive of applicable taxes, including Goods and Services Tax (GST) as applicable under Indian law. The Customer is responsible for paying all applicable taxes.",
                    ],
                    [
                      "4.4 Refunds",
                      <>
                        Monthly subscriptions may be cancelled at any time; no
                        partial-month refunds are issued. If you believe you
                        have been charged in error, contact{" "}
                        <a
                          href="mailto:support@maltora.app"
                          style={{
                            color: "var(--teal)",
                            textDecoration: "none",
                            fontWeight: "500",
                          }}
                        >
                          support@maltora.app
                        </a>{" "}
                        within 14 days of the charge.
                      </>,
                    ],
                    [
                      "4.5 Usage Limits",
                      "If your usage exceeds the order volume included in your plan, Maltora will notify you. Your service will not be suspended mid-cycle; however, Maltora may recommend an upgrade if overages are consistent.",
                    ],
                    [
                      "4.6 Free Trial",
                      "Maltora may offer a 14-day free trial at its discretion. No credit card is required during the trial period. At the end of the trial, you must subscribe to a paid plan to continue using the Service. Trial data is retained for 30 days after trial expiry.",
                    ],
                  ] as [string, React.ReactNode][]
                ).map(([title, body]) => (
                  <div key={title as string}>
                    <h3 style={h3Style}>{title}</h3>
                    <p style={p}>{body}</p>
                  </div>
                ))}
              </section>
              {divider}

              <section id="platform-use">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  5. Acceptable Use
                </h2>
                <h3 style={h3Style}>5.1 Permitted Use</h3>
                <p style={p}>
                  You may use the platform solely for the internal business
                  operations of your beverage or distribution business, in
                  accordance with these Terms and all applicable laws, including
                  excise, licensing, and financial regulations applicable to the
                  beverage industry in India.
                </p>
                <h3 style={h3Style}>5.2 Prohibited Conduct</h3>
                <p style={p}>You agree not to:</p>
                <ul style={{ paddingLeft: "20px", marginBottom: "14px" }}>
                  {[
                    "Use the Service to facilitate any unlicensed or illegal sale, distribution, or manufacture of beverages.",
                    "Reverse engineer, decompile, or attempt to extract the source code of the platform.",
                    "Resell, sublicense, or provide the Service to third parties without Maltora's written consent.",
                    "Upload malicious code, viruses, or content that infringes a third party's intellectual property rights.",
                    "Attempt to gain unauthorised access to other customers' data or Maltora's systems.",
                    "Use the platform in a manner that disrupts or degrades performance for other users.",
                  ].map((item) => (
                    <li key={item} style={{ ...p, marginBottom: "6px" }}>
                      {item}
                    </li>
                  ))}
                </ul>
                <h3 style={h3Style}>5.3 Regulatory Compliance</h3>
                <p style={p}>
                  The Customer is solely responsible for ensuring its use of
                  Maltora complies with all applicable beverage industry
                  regulations, excise duties, GST obligations, licensing
                  requirements, and any other laws in its operating
                  jurisdiction. Maltora does not provide legal, tax, or
                  compliance advice.
                </p>
              </section>
              {divider}

              <section id="data-ownership">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  6. Data Ownership &amp; Processing
                </h2>
                {(
                  [
                    [
                      "6.1 Your Data is Yours",
                      "All Customer Data you upload or generate within Maltora remains your property at all times. Maltora does not claim ownership over your order records, inventory data, buyer information, or financial data.",
                    ],
                    [
                      "6.2 Licence to Process",
                      "You grant Maltora a limited, non-exclusive licence to process Customer Data solely for the purpose of providing and improving the Service to you.",
                    ],
                    [
                      "6.3 Data Export",
                      <>
                        You may export your data at any time via the
                        platform&apos;s export tools. On termination, Maltora
                        will make your data available for export for 30 days,
                        after which it may be deleted in accordance with our{" "}
                        <Link
                          to="/privacy"
                          style={{
                            color: "var(--teal)",
                            textDecoration: "none",
                            fontWeight: "500",
                          }}
                        >
                          Privacy Policy
                        </Link>
                        .
                      </>,
                    ],
                    [
                      "6.4 Aggregated Analytics",
                      "Maltora may use anonymised, aggregated data derived from usage patterns (never individual Customer Data) to improve the platform, publish industry benchmarks, or develop new features. Such data cannot be used to identify you or your customers.",
                    ],
                  ] as [string, React.ReactNode][]
                ).map(([title, body]) => (
                  <div key={title as string}>
                    <h3 style={h3Style}>{title}</h3>
                    <p style={p}>{body}</p>
                  </div>
                ))}
              </section>
              {divider}

              <section id="confidentiality">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  7. Confidentiality
                </h2>
                <p style={p}>
                  Each party agrees to keep confidential any non-public
                  information disclosed by the other party that is marked as
                  confidential or would reasonably be understood to be
                  confidential (&quot;Confidential Information&quot;). This
                  obligation does not apply to information that is publicly
                  known, independently developed, or required to be disclosed by
                  law.
                </p>
                <p style={p}>
                  Maltora treats all Customer Data as Confidential Information
                  and will not disclose it to third parties except as described
                  in our{" "}
                  <Link
                    to="/privacy"
                    state={{ fromApp: true }}
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    Privacy Policy
                  </Link>{" "}
                  or as required by applicable law.
                </p>
              </section>
              {divider}

              <section id="ip">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  8. Intellectual Property
                </h2>
                <h3 style={h3Style}>8.1 Maltora&apos;s IP</h3>
                <p style={p}>
                  The Maltora platform, including its software, design,
                  trademarks, and all content created by Maltora, is owned by or
                  licensed to Maltora. Nothing in these Terms transfers any
                  ownership of Maltora&apos;s intellectual property to you.
                </p>
                <h3 style={h3Style}>8.2 Feedback</h3>
                <p style={p}>
                  If you submit suggestions, ideas, or feedback about the
                  Service, you grant Maltora a perpetual, royalty-free licence
                  to use that feedback without any obligation to compensate you.
                </p>
              </section>
              {divider}

              <section id="warranties">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  9. Warranties &amp; Disclaimers
                </h2>
                <p style={p}>
                  Maltora warrants that it will provide the Service with
                  reasonable skill and care and will make commercially
                  reasonable efforts to maintain platform availability. Our
                  target uptime is 99.5% on a monthly basis, excluding scheduled
                  maintenance windows.
                </p>
                <p style={p}>
                  Except as expressly stated, the Service is provided &quot;as
                  is&quot; and &quot;as available&quot;. Maltora does not
                  warrant that the Service will be error-free, uninterrupted, or
                  suitable for any particular regulatory compliance purpose. The
                  Customer is solely responsible for verifying that outputs from
                  the platform meet its regulatory and business requirements
                  before relying on them.
                </p>
              </section>
              {divider}

              <section id="liability">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  10. Limitation of Liability
                </h2>
                <p style={p}>
                  To the maximum extent permitted by applicable law,
                  Maltora&apos;s total liability to the Customer for any claim
                  arising from or related to these Terms or the Service will not
                  exceed the total fees paid by the Customer in the three months
                  immediately preceding the claim.
                </p>
                <p style={p}>
                  In no event will Maltora be liable for indirect, incidental,
                  consequential, or punitive damages, including loss of revenue,
                  loss of profit, or loss of data, even if Maltora has been
                  advised of the possibility of such damages.
                </p>
                <p style={p}>
                  These limitations do not apply to liability arising from
                  fraud, gross negligence, or wilful misconduct by Maltora.
                </p>
              </section>
              {divider}

              <section id="termination">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  11. Termination
                </h2>
                <h3 style={h3Style}>11.1 By You</h3>
                <p style={p}>
                  You may cancel your subscription at any time from your account
                  settings. Cancellation takes effect at the end of the current
                  billing period. You will retain access to the platform until
                  the end of the paid period.
                </p>
                <h3 style={h3Style}>11.2 By Maltora</h3>
                <p style={p}>
                  Maltora may suspend or terminate your account if you
                  materially breach these Terms and fail to remedy the breach
                  within 14 days of written notice. Maltora may terminate
                  immediately if the breach involves illegal activity, data
                  security risks, or non-payment.
                </p>
                <h3 style={h3Style}>11.3 Effect of Termination</h3>
                <p style={p}>
                  On termination, your right to access the platform ceases.
                  Customer Data will be available for export for 30 days
                  following termination. Provisions that by their nature should
                  survive termination (including Sections 6, 7, 8, 10, and 12)
                  will continue to apply.
                </p>
              </section>
              {divider}

              <section id="governing-law">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  12. Governing Law &amp; Dispute Resolution
                </h2>
                <p style={p}>
                  These Terms are governed by the laws of India. Any disputes
                  arising from or in connection with these Terms shall be
                  subject to the exclusive jurisdiction of the courts in
                  Bangalore, Karnataka, India.
                </p>
                <p style={p}>
                  Before initiating legal proceedings, the parties agree to
                  attempt to resolve any dispute in good faith through direct
                  negotiation for a period of 30 days.
                </p>
              </section>
              {divider}

              <section id="changes">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>
                  13. Changes to These Terms
                </h2>
                <p style={p}>
                  Maltora may update these Terms from time to time. Where
                  changes are material, we will notify Customers by email at
                  least 14 days before the changes take effect. Continued use of
                  the Service after that date constitutes acceptance of the
                  revised Terms.
                </p>
              </section>
              {divider}

              <section id="contact-legal">
                <h2 style={{ ...h2Style, margin: "0 0 12px" }}>14. Contact</h2>
                <p style={p}>
                  For any questions about these Terms, please contact us at:
                </p>
                <p style={p}>
                  <strong>Maltora Support</strong>
                  <br />
                  Email:{" "}
                  <a
                    href="mailto:support@maltora.app"
                    style={{
                      color: "var(--teal)",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    support@maltora.app
                  </a>
                </p>
                <p style={p}>
                  <Link
                    to="/privacy"
                    state={{ fromApp: true }}
                    style={{ color: "var(--teal)" }}
                  >
                    Privacy Policy
                  </Link>
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
